import { defer } from 'lodash';

// Constants
import { TIMES } from '../../../common/utils/timeUtil';

/**
 * Add a cookie banner overlay element, that will span the page, and prevent users
 * from interacting with page before interacting with banner
 */
export const addCookieBannerPageOverlay = () => {
    const darkFilter = document.createElement('div');
    darkFilter.classList.add('onetrust-banner-dark-filter');

    document.body.append(darkFilter);
};

/**
 * Determine user's consent requirement and add/remove `requires-consent` to/from class name of
 * OneTrust top-most element
 */
export const describeCookieBannerRequirement = () => {
    const consentBannerElement = document.getElementById('onetrust-banner-sdk');

    // eslint-disable-next-line new-cap
    const domainData = window.OneTrust.GetDomainData();
    const consentModel = domainData?.ConsentModel?.Name;

    // For now, use consent model to determine whether consent banner needs to be displayed.
    // This might change in the future, e.g. CCPA banner would need to be shown even when its consent is opt-out.
    const requireConsent = consentModel === 'opt-in';

    if (requireConsent) {
        document.body.classList.add('ot-requires-consent');

        // Focus on banner once the call stack is cleared.
        // Deferring is required to make sure that the banner is focused, as sometimes focus on
        // form input (in login and register pages) is called first
        defer(() => consentBannerElement?.focus());
    } else {
        document.body.classList.add('ot-optional-consent');
    }
};

/**
 * Clean up the styles when OneTrust modals have been closed or OneTrust hasn't loaded correctly.
 */
export const removeCookieBannerStyles = () => {
    // Remove any OneTrust classes from the document body
    document.body.classList.remove('ot-awaiting-consent');
    document.body.classList.remove('ot-requires-consent');
    document.body.classList.remove('ot-optional-consent');

    // Remove the "ot-prompt-shown" class after a short timeout to ensure the modal animation has played
    setTimeout(() => {
        document.body.classList.remove('ot-prompt-shown');
    }, TIMES.SECOND);
};

/**
 * Determine user's cookie banner state and add/remove `consent-choice-pending` to/from class name
 * of OneTrust top-most element
 */
export const describeCookieBannerChoiceState = () => {
    // eslint-disable-next-line new-cap
    const showCookieBanner = !window.OneTrust.IsAlertBoxClosed();
    if (showCookieBanner) {
        // Add a class to the body to indicate that a OneTrust prompt has been shown to the user
        document.body.classList.add('ot-prompt-shown');
    } else {
        removeCookieBannerStyles();
    }
};

/**
 * Used to reload OneTrust banner, which will be triggered when user logs in/out
 * - If user has local cookie preference, but do not have saved cookie preference on OneTrust Cloud, reloading
 *   the banner will save user's cookie preference to OneTrust Cloud
 *
 * Code taken from https://community.cookiepro.com/s/article/UUID-69162cb7-c4a2-ac70-39a1-ca69c9340046?language=en_US
 */
export const reloadOneTrustBanner = () => {
    const otConsentSdk = document.getElementById('onetrust-consent-sdk');
    if (otConsentSdk) {
        otConsentSdk.remove();
    }

    if (window.OneTrust === null) return;

    // eslint-disable-next-line new-cap
    window.OneTrust.Init();

    setTimeout(() => {
        // eslint-disable-next-line new-cap
        window.OneTrust.LoadBanner();

        const toggleDisplay = document.getElementsByClassName('ot-sdk-show-settings');

        for (let i = 0; i < toggleDisplay.length; i++) {
            toggleDisplay[i].onclick = (event) => {
                event.stopImmediatePropagation();
                // eslint-disable-next-line new-cap
                window.OneTrust.ToggleInfoDisplay();
            };
        }
    }, 1000);
};

/**
 * Check whether a specific cookie group is allowed
 *
 * @param cookieGroupId Cookie category to check
 * @returns {boolean|null}
 */
export const cookieGroupAllowed = (cookieGroupId) => {
    if (!window.OnetrustActiveGroups) return null;

    return window.OnetrustActiveGroups.includes(cookieGroupId);
};
